import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { Paging } from "types/common.types";

import { RootState } from "store/store";

import {
  createWorkspace,
  deleteUserFromWorkspace,
  deleteWorkspace,
  getAllWorkspaces,
  getUserWorkspaces,
  getWorkspace,
  linkAnotherWorkspace,
  linkAnotherWorkspaceStatus,
  removeWorkspaceLink,
  updateWorkspace,
  updateWorkspaceLogo,
} from "./actions";
import { Workspace } from "./types";

export type WorkspacesState = {
  loading: boolean;
  error: any;
  meta: Paging;
};

export const workspaceAdapter = createEntityAdapter<Workspace>();

export const workspacesSlice = createSlice({
  name: "workspaces",
  initialState: workspaceAdapter.getInitialState<WorkspacesState>({
    loading: false,
    error: false,
    meta: {} as Paging,
  }),
  reducers: {
    workspacesSetAll: workspaceAdapter.setAll,
    workspaceAdd: workspaceAdapter.addOne,
    workspaceRemove: workspaceAdapter.removeOne,
    workspaceUpdate: workspaceAdapter.updateOne,
    workspaceAddOne: workspaceAdapter.addOne,
    workspaceSetOne: workspaceAdapter.setOne,
  },
  extraReducers: (builder) => {
    //All workspaces - /workspaces
    builder.addCase(getAllWorkspaces.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getAllWorkspaces.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getAllWorkspaces.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.meta = action.payload.meta;
      workspaceAdapter.setAll(state, action.payload.data);
    });
    //User workspaces - /my/workspaces
    builder.addCase(getUserWorkspaces.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getUserWorkspaces.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getUserWorkspaces.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      state.meta = action.payload.meta;
      workspaceAdapter.setAll(state, action.payload.data);
    });
    builder.addCase(createWorkspace.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(createWorkspace.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(createWorkspace.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      workspaceAdapter.addOne(state, action.payload.data);
    });
    builder.addCase(deleteWorkspace.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(deleteWorkspace.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(deleteWorkspace.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      workspaceAdapter.removeOne(state, action.meta.arg);
    });
    builder.addCase(getWorkspace.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getWorkspace.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getWorkspace.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      workspaceAdapter.setOne(state, action.payload.data);
    });
    builder.addCase(updateWorkspaceLogo.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(updateWorkspaceLogo.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(updateWorkspaceLogo.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      workspaceAdapter.upsertOne(state, action.payload.data);
    });
    builder.addCase(updateWorkspace.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(updateWorkspace.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(updateWorkspace.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      workspaceAdapter.upsertOne(state, action.payload.data);
    });
    builder.addCase(deleteUserFromWorkspace.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(deleteUserFromWorkspace.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(deleteUserFromWorkspace.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      workspaceAdapter.upsertOne(state, action.payload.data);
    });

    // link workspace
    builder.addCase(linkAnotherWorkspace.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(linkAnotherWorkspace.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(linkAnotherWorkspace.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      workspaceAdapter.setOne(state, action.payload.data);
    });

    // link workspace status
    builder.addCase(linkAnotherWorkspaceStatus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(linkAnotherWorkspaceStatus.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(linkAnotherWorkspaceStatus.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      workspaceAdapter.setOne(state, action.payload.data);
    });

    // delete workspace link
    builder.addCase(removeWorkspaceLink.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(removeWorkspaceLink.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(removeWorkspaceLink.fulfilled, (state, action) => {
      state.loading = false;
      state.error = false;
      workspaceAdapter.setOne(state, action.payload.data);
    });
  },
});

// selectors

export const {
  selectAll: selectAllWorkspaces,
  selectById: selectWorkspaceById,
} = workspaceAdapter.getSelectors<RootState>((state) => state.workspaces);
export const workspacesLoading = (state: RootState) => state.workspaces.loading;

// Action creators are generated for each case reducer function
// export const {} = workspacesSlice.actions;
export default workspacesSlice.reducer;
